const WISHLIST_BUTTON_SELECTOR = '[data-wishlist-button]';
const ACTIVE_CLASS = 'is-active';
const LOADING_CLASS = 'is-loading';
const WISHLIST_EVENT_NAME = 'wishlist:changed';
const ACCOUNT_POPOVER_SELECTOR = '.account-popover';
const ACCOUNT_DRAWER_SELECTOR = '.account-drawer';
const WISHLIST_ACCOUNT_HOST_SELECTOR = '[data-wishlist-account-host]';
const WISHLIST_ACCOUNT_ELEMENT_SELECTOR = `${WISHLIST_ACCOUNT_HOST_SELECTOR} shopify-account`;
const WISHLIST_LOGIN_TRIGGER_SELECTOR = '[data-wishlist-login-open-account]';


document.addEventListener('click', async (event) => {
  const target = event.target;

  if (!(target instanceof Element)) {
    return;
  }

  const loginTrigger = target.closest(WISHLIST_LOGIN_TRIGGER_SELECTOR);

  if (loginTrigger instanceof HTMLButtonElement) {
    event.preventDefault();
    event.stopPropagation();
    await openWishlistNativeAccountFromDialog();
    return;
  }

  const button = target.closest(WISHLIST_BUTTON_SELECTOR);

  if (!(button instanceof HTMLButtonElement)) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  if (button.classList.contains(LOADING_CLASS)) {
    return;
  }

  if (button.dataset.customerLoggedIn !== 'true') {
    await openWishlistLoginPopover(button);
    return;
  }

  const productId = button.dataset.productId;
  const isActive = button.classList.contains(ACTIVE_CLASS);
  const insertUrl = button.dataset.proxyInsertUrl || button.dataset.proxyUrl || '/apps/api/customers/wishlist-list/insert';
  const removeUrl = button.dataset.proxyRemoveUrl || '/apps/api/customers/wishlist-list/remove';
  const proxyUrl = isActive ? removeUrl : insertUrl;
  const listName = button.dataset.listName;

  if (!productId) {
    console.error('Wishlist button is missing data-product-id.');
    return;
  }

  setButtonState(button, { loading: true });

  try {
    const response = await fetch(proxyUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        product_id: productId,
        ...(listName ? { list_name: listName } : {}),
      }),
    });

    if (!response.ok) {
      const errorPayload = await response.json().catch(() => null);

      throw new Error(errorPayload?.error || errorPayload?.message || 'Wishlist request failed.');
    }

    setButtonState(button, { active: !isActive });
    document.dispatchEvent(
      new CustomEvent(WISHLIST_EVENT_NAME, {
        detail: {
          action: isActive ? 'remove' : 'insert',
          productId,
          button,
        },
      })
    );
  } catch (error) {
    console.error(`Error ${isActive ? 'removing product from' : 'adding product to'} wishlist:`, error);
    setButtonState(button, { active: isActive });
  } finally {
    button.classList.remove(LOADING_CLASS);
  }
});

function setButtonState(button, { active = false, loading = false }) {
  button.classList.toggle(ACTIVE_CLASS, active);
  button.classList.toggle(LOADING_CLASS, loading);
  button.setAttribute('aria-pressed', active ? 'true' : 'false');
  button.setAttribute('aria-busy', loading ? 'true' : 'false');

  const activeLabel = button.dataset.activeLabel;
  const inactiveLabel = button.dataset.inactiveLabel;
  const label = active ? activeLabel : inactiveLabel;

  if (label) {
    button.setAttribute('aria-label', label);
  }
}
async function openWishlistLoginPopover(button) {
  const host = document.querySelector(WISHLIST_ACCOUNT_HOST_SELECTOR);

  if (!(host instanceof HTMLElement) || typeof host.showDialog !== 'function') {
    return false;
  }

  host.showDialog();
  return true;
}

async function openWishlistNativeAccountFromDialog() {
  const host = document.querySelector(WISHLIST_ACCOUNT_HOST_SELECTOR);

  if (!(host instanceof HTMLElement)) {
    return false;
  }

  if (typeof host.closeDialog === 'function') {
    await host.closeDialog();
  }

  if (openNativeAccountSurface()) {
    return true;
  }

  const hostAwakened = await awakenWishlistAccountHost();

  if (!hostAwakened) {
    return false;
  }

  if (openNativeAccountSurface()) {
    return true;
  }

  return waitForNativeAccountSurface();
}

function openNativeAccountSurface() {
  const accountPopover = document.querySelector(ACCOUNT_POPOVER_SELECTOR);

  if (accountPopover instanceof HTMLElement) {
    if (isPopoverOpen(accountPopover)) {
      return true;
    }

    if (openAccountPopover(accountPopover)) {
      return true;
    }
  }

  const accountDrawer = document.querySelector(ACCOUNT_DRAWER_SELECTOR);

  if (accountDrawer instanceof HTMLElement) {
    if (isAccountDrawerOpen(accountDrawer)) {
      return true;
    }

    if (openAccountDrawer(accountDrawer)) {
      return true;
    }
  }

  return false;
}

function openAccountPopover(popover) {
  if (isPopoverOpen(popover)) {
    return true;
  }

  if (typeof popover.showPopover === 'function') {
    try {
      popover.showPopover();
      return true;
    } catch (error) {
      console.warn('Unable to open account popover with showPopover().', error);
    }
  }

  popover.setAttribute('open', '');
  return isPopoverOpen(popover);
}

function openAccountDrawer(drawer) {
  if (isAccountDrawerOpen(drawer)) {
    return true;
  }

  if (typeof drawer.showDialog === 'function') {
    try {
      drawer.showDialog();
      return true;
    } catch (error) {
      console.warn('Unable to open account drawer with showDialog().', error);
    }
  }

  if (typeof drawer.showModal === 'function') {
    try {
      drawer.showModal();
      return true;
    } catch (error) {
      console.warn('Unable to open account drawer with showModal().', error);
    }
  }

  drawer.setAttribute('open', '');
  return isAccountDrawerOpen(drawer);
}

function isPopoverOpen(popover) {
  if (popover.hasAttribute('open')) {
    return true;
  }

  if (typeof popover.matches === 'function') {
    try {
      return popover.matches(':popover-open');
    } catch {
      return false;
    }
  }

  return false;
}

function isAccountDrawerOpen(drawer) {
  return drawer.hasAttribute('open');
}

async function waitForNativeAccountSurface(timeoutMs = 800) {
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const accountPopover = document.querySelector(ACCOUNT_POPOVER_SELECTOR);

    if (accountPopover instanceof HTMLElement && isPopoverOpen(accountPopover)) {
      return true;
    }

    const accountDrawer = document.querySelector(ACCOUNT_DRAWER_SELECTOR);

    if (accountDrawer instanceof HTMLElement && isAccountDrawerOpen(accountDrawer)) {
      return true;
    }

    await wait(50);
  }

  return false;
}

async function awakenWishlistAccountHost() {
  const host = document.querySelector(WISHLIST_ACCOUNT_HOST_SELECTOR);
  const accountElement = document.querySelector(WISHLIST_ACCOUNT_ELEMENT_SELECTOR);

  if (!(host instanceof HTMLElement) || !(accountElement instanceof HTMLElement)) {
    return false;
  }

  if (
    typeof customElements !== 'undefined' &&
    typeof customElements.whenDefined === 'function' &&
    typeof accountElement.matches === 'function' &&
    accountElement.matches(':not(:defined)')
  ) {
    try {
      await customElements.whenDefined('shopify-account');
    } catch (error) {
      console.warn('shopify-account did not finish defining before wishlist login fallback.', error);
    }
  }

  await nextAnimationFrame();

  const trigger = findWishlistAccountTrigger(accountElement);

  if (trigger instanceof HTMLElement) {
    triggerAccountElement(trigger);
  } else {
    triggerAccountElement(accountElement);
  }

  await nextAnimationFrame();
  await nextAnimationFrame();

  return true;
}

function findWishlistAccountTrigger(accountElement) {
  const shadowRoot = accountElement.shadowRoot;

  if (shadowRoot instanceof ShadowRoot) {
    const shadowTrigger = shadowRoot.querySelector('button, [role="button"], [popovertarget], [aria-haspopup]');

    if (shadowTrigger instanceof HTMLElement) {
      return shadowTrigger;
    }
  }

  const slottedTrigger = accountElement.querySelector('[slot="signed-out-avatar"]');

  if (slottedTrigger instanceof HTMLElement) {
    return slottedTrigger;
  }

  return null;
}

function triggerAccountElement(element) {
  if (typeof PointerEvent === 'function') {
    element.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true, cancelable: true, composed: true }));
    element.dispatchEvent(new PointerEvent('pointerup', { bubbles: true, cancelable: true, composed: true }));
  }

  if (typeof element.click === 'function') {
    element.click();
    return;
  }

  element.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, composed: true }));
}

function syncWishlistAccountHostPosition(button) {
  const host = document.querySelector(WISHLIST_ACCOUNT_HOST_SELECTOR);

  if (!(host instanceof HTMLElement)) {
    return;
  }

  const stickyHeader = document.querySelector('.header[data-sticky-state="active"]');
  const accountOffsetTop = stickyHeader
    ? 'calc(var(--header-height) - 1px)'
    : 'calc(var(--header-group-height) + (var(--header-height) * var(--transparent-header-offset-boolean)))';

  host.style.setProperty('--account-offset-top', accountOffsetTop);

  if (button instanceof HTMLElement) {
    const buttonRect = button.getBoundingClientRect();
    host.style.setProperty('--wishlist-account-top', `${Math.max(buttonRect.top, 0)}px`);
    host.style.setProperty('--wishlist-account-left', `${Math.max(buttonRect.left, 0)}px`);
    host.style.setProperty('--wishlist-account-width', `${Math.max(buttonRect.width, 44)}px`);
    host.style.setProperty('--wishlist-account-height', `${Math.max(buttonRect.height, 44)}px`);
  }
}

function nextAnimationFrame() {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
}

function wait(duration) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, duration);
  });
}

